import SFlex from 'components/opinionless/SFlex'
import { useTranslation } from 'i18n'
import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import ConditionalRender from 'utils/conditionalRender'
import { ga4_cardClicked } from 'utils/ga4'
import FCheckBox from '../FCheckBox'
import FCard from './FCard'
import { CardSelectStyledContainer } from './styles'
import { useSTheme } from 'styles/theme'

interface Props {
    cardDataArray: CardDataItem[]
    noneOptionLabel?: string
    noneChecked?: boolean
    id: string
    name: string
    minimumSelection: number
}

type CardDataItem = {
    text: string
    imageUrl: string
    checked?: boolean
    id: string
}

const FCardSelect: React.FC<Props> = (props: Props) => {
    const { register, setValue, setError, clearErrors, errors, watch } = useFormContext()
    const [checkedCards, setCheckedCards] = useState<string[]>(
        props.cardDataArray
            .filter((c) => c.checked)
            .map((c) => c.id)
            .concat(props.noneChecked ? ['NONE'] : [])
    )

    useEffect(() => {
        setCheckedCards(
            props.cardDataArray
                .filter((c) => c.checked)
                .map((c) => c.id)
                .concat(props.noneChecked ? ['NONE'] : [])
        )
    }, [props.noneChecked])

    const { t } = useTranslation(window.LANG_NS)
    watch(props.name)
    const theme = useSTheme()

    const validateMinimumSelection = () => {
        if (checkedCards.length >= props.minimumSelection) {
            clearErrors(props.name)
        }
    }

    const onCardCheckChange = (id: string, checked: boolean) => {
        let checkedAux = JSON.parse(JSON.stringify(checkedCards))
        if (id === 'NONE') {
            checked ? (checkedAux = ['NONE']) : checkedAux.splice(checkedAux.indexOf(id), 1)
            setCheckedCards(checkedAux)
        } else {
            if (checkedAux.includes('NONE')) {
                checkedAux.splice(checkedAux.indexOf('NONE'), 1)
            }
            checked ? checkedAux.push(id) : checkedAux.splice(checkedAux.indexOf(id), 1)
            setCheckedCards(checkedAux)
        }
    }

    const onNoneToggled = (isNoneSelected: boolean) => {
        onCardCheckChange('NONE', isNoneSelected)
    }

    useEffect(() => {
        setValue(props.name, checkedCards, { shouldDirty: true })
        validateMinimumSelection()
    }, [checkedCards])

    useEffect(() => {
        register(props.name)
    }, [])

    return (
        <React.Fragment>
            <CardSelectStyledContainer
                id={props.id}
                onClick={() => setValue(props.name, checkedCards, { shouldDirty: true })}
            >
                {props.cardDataArray.map((card: CardDataItem, index: number) => (
                    <FCard
                        id={card.id}
                        key={index}
                        imageUrl={card.imageUrl}
                        text={card.text}
                        isChecked={checkedCards.includes(card.id)}
                        onCheckChange={(id, e) => {
                            ga4_cardClicked(card.text)
                            onCardCheckChange(id, e)
                        }}
                    />
                ))}
            </CardSelectStyledContainer>
            <ConditionalRender if={props.noneOptionLabel}>
                <SFlex pt={errors?.[props.name]?.message ? 3 : 0}>
                    <FCheckBox
                        name={props.name}
                        id={`${props.id}_none`}
                        value={'NONE'}
                        isChecked={checkedCards.includes('NONE')}
                        text={props.noneOptionLabel}
                        onChange={onNoneToggled}
                        color={theme.colors.lbBlue}
                    />
                </SFlex>
            </ConditionalRender>
        </React.Fragment>
    )
}

export default FCardSelect
